import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuthenticated } from "../../../authn";
import { useUserOrgs } from "./useUserOrgs";
import { Org, OrgKey } from "../../../generated/core-api";

const STORAGE_KEY_PREFIX = "user_org_preferences_";

const getStorageKey = (userId: string) => `${STORAGE_KEY_PREFIX}${userId}`;

const getStoredPreferences = (userId: string): OrgKey[] => {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const setStoredPreferences = (userId: string, preferences: OrgKey[]) => {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(preferences));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

const orgToOrgKey = (org: Org): OrgKey => ({
  id: org.id,
  category: org.category,
});

/**
 * Hook for managing user organization preferences stored in local storage.
 * Preferences are used to determine which orgs are included by default when creating new threads.
 *
 * Uses tanstack query/mutation for consistent async state management and error handling.
 * Provides a single setPreferences mutation for form-based updates.
 */
export function useUserOrgPreferences() {
  const { userId } = useAuthenticated();
  const queryClient = useQueryClient();

  // Get user's available orgs - this must succeed before preferences can be computed
  const userOrgsQuery = useUserOrgs(userId);

  // Use useQuery for consistency with other tanstack hooks and to handle dependencies properly
  const preferencesQuery = useQuery({
    queryKey: ["userOrgPreferences", userId],
    queryFn: () => {
      const userOrgs = userOrgsQuery.data || [];
      const storedPreferences = getStoredPreferences(userId);

      // If no preferences stored, default to all user orgs
      if (storedPreferences.length === 0) {
        return userOrgs.map(orgToOrgKey);
      }

      // Filter out preferences for orgs the user no longer has access to
      // Note: We don't update localStorage here to avoid write-on-read antipattern
      const availableOrgIds = new Set(userOrgs.map((org) => org.id));
      const validPreferences = storedPreferences.filter((pref) =>
        availableOrgIds.has(pref.id)
      );

      return validPreferences;
    },
    // Only run this query when userOrgs has successfully loaded
    enabled: userOrgsQuery.isSuccess && !!userOrgsQuery.data,
    staleTime: 0, // Always check localStorage on mount
  });

  const setPreferencesMutation = useMutation({
    mutationFn: async (newPreferences: OrgKey[]) => {
      const userOrgs = userOrgsQuery.data || [];
      const availableOrgIds = new Set(userOrgs.map((org) => org.id));

      // Filter to only include orgs the user has access to
      const validPreferences = newPreferences.filter((pref) =>
        availableOrgIds.has(pref.id)
      );

      // Update localStorage
      setStoredPreferences(userId, validPreferences);
      return validPreferences;
    },
    onSuccess: () => {
      // Invalidate the query to trigger a refetch
      queryClient.invalidateQueries({
        queryKey: ["userOrgPreferences", userId],
      });
    },
  });

  // Compute derived states
  const preferences = preferencesQuery.data || [];
  const userOrgs = userOrgsQuery.data || [];

  const isLoading =
    userOrgsQuery.isLoading ||
    (userOrgsQuery.isSuccess && preferencesQuery.isLoading);
  const isError = userOrgsQuery.isError || preferencesQuery.isError;
  const error = userOrgsQuery.error || preferencesQuery.error;

  return {
    preferences,
    userOrgs,
    isLoading,
    isError,
    error,
    setPreferences: setPreferencesMutation,
  };
}
